import { useCallback, type PointerEvent, type ReactNode } from 'react'
import { useI18n } from './i18n'

export const SIDE_MIN = 240
export const SIDE_MAX = 560
export const SIDE_DEFAULT = 320

type Props = {
  open: boolean
  width: number
  onToggle: () => void
  onResize: (width: number) => void
  children: ReactNode
  /** 見出しと本体のあいだに貼り付けるもの。本体をスクロールしても動かない（準備物パネル） */
  top?: ReactNode
}

/**
 * マップタブ右の一覧パネル。開閉と幅ドラッグだけを持ち、中身は問わない。
 * 幅・開閉ともクライアントごとのローカル状態で、他の人には伝わらない。
 */
export function SidePanel({ open, width, onToggle, onResize, children, top }: Props) {
  const { t } = useI18n()
  // 左端のハンドルを掴んで横に引く。左へ引くと広がるので符号が反転する
  const startResize = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      const handle = e.currentTarget
      const startX = e.clientX
      const startWidth = width
      handle.setPointerCapture(e.pointerId)

      const move = (ev: globalThis.PointerEvent) => {
        const next = startWidth + (startX - ev.clientX)
        onResize(Math.max(SIDE_MIN, Math.min(SIDE_MAX, Math.round(next))))
      }
      const stop = () => {
        handle.removeEventListener('pointermove', move)
        handle.removeEventListener('pointerup', stop)
        handle.removeEventListener('pointercancel', stop)
      }
      handle.addEventListener('pointermove', move)
      handle.addEventListener('pointerup', stop)
      handle.addEventListener('pointercancel', stop)
    },
    [width, onResize],
  )

  // 閉じているときの「⟨ 一覧」はマップ右上のボタン群（App の .map-tools）が持つ。
  // メニュー非表示ボタンと横に並べるため、こちらでは何も描かない
  if (!open) return null

  return (
    <aside className="side" style={{ width }}>
      <div
        className="side__grip"
        onPointerDown={startResize}
        role="separator"
        aria-label={t('side.width')}
        aria-orientation="vertical"
      />
      <header className="side__head">
        <span className="side__title">{t('side.title')}</span>
        <button type="button" className="icon-btn" onClick={onToggle} title={t('app.close')} aria-label={t('side.close')}>
          ⟩
        </button>
      </header>
      {top && <div className="side__top">{top}</div>}
      <div className="side__body">{children}</div>
    </aside>
  )
}
